import { CommonModule } from '@angular/common'
import { Component, EventEmitter, Input, Output } from '@angular/core'
import { IAutomationFormFields } from '@employer/app/components/jobs-automation/job-automation-form.component'
import { ISendMessageFormData } from '@employer/app/components/jobs-automation/job-automation.model'
import { IAutomatedQuestionSendMessage } from '@employer/app/modules/customer/views/jobs-post-automation-company-settings/auto-question-message.model'
import { JobsPostAutomationCompanySettingsStore } from '@employer/app/modules/customer/views/jobs-post-automation-company-settings/jobs-post-automation-company-settings.store'
import { E11ConfirmationModule } from '@engineering11/ui-lib/e11-confirmation'
import { E11TooltipModule } from '@engineering11/ui-lib/e11-tooltip'
import { deepCopy } from '@engineering11/utility'
import { LetDirective } from '@ngrx/component'
import { TranslateModule } from '@ngx-translate/core'
import { IAutomationFormData } from '../automation-form.model'
import { IRequestUpdatesFormData } from '../automation-request-updates/auto-request-updates.model'

export type AutomationFormData = ISendMessageFormData | IRequestUpdatesFormData | IAutomatedQuestionSendMessage
export type FormField = keyof IAutomationFormFields | 'autoQuestionInitial' | 'autoQuestionFollowUp' | 'autoQuestionWarning'

@Component({
  selector: 'automation-reset',
  standalone: true,
  imports: [TranslateModule, E11TooltipModule, LetDirective, CommonModule, E11ConfirmationModule],
  template: `
    <ng-container *ngrxLet="companyAutomationFormData$ as formData">
      <div class="e11-flex e11-gap-4 e11-text-sm">
        <e11-tooltip
          [color]="'dark-grey'"
          [tooltipText]="'This will reset the message content to what has been configured globally under your Company Settings.' | translate"
        >
          <button
            *ngIf="!loading && formData"
            (click)="confirmAutomationDeletion.open()"
            type="button"
            class="e11-text-skin-primary-accent e11-flex e11-items-center"
          >
            <span class="material-icons-outlined md-18">replay</span>
            Reset
          </button>
        </e11-tooltip>
      </div>

      <e11-confirmation
        #confirmAutomationDeletion
        (answer)="onResetFormData(formData, $event)"
        [backdropStyle]="'dark'"
        cancelText="{{ 'Cancel' | translate }}"
        confirmText="{{ 'Confirm' | translate }}"
        confirmTitle="{{ 'Reset message content?' | translate }}"
      >
        {{
          'You are about to reset your message text and attachments back to what has been configured in your Company Settings defaults. Do you want to continue?'
            | translate
        }}
      </e11-confirmation>
    </ng-container>
  `,
  styles: ``,
})
export class AutomationResetComponent {
  @Input({ required: true }) formField!: FormField
  @Input() loading = true

  @Output() formFieldReset = new EventEmitter<AutomationFormData>()

  companyAutomationFormData$ = this.jobsPostAutomationCompanySettingsStore.formData$

  constructor(private jobsPostAutomationCompanySettingsStore: JobsPostAutomationCompanySettingsStore) {}

  onResetFormData(companyData: IAutomationFormData, confirm: boolean) {
    if (!confirm) return
    const companyFormData = deepCopy(companyData)
    if (!companyFormData) return
    switch (this.formField) {
      case 'autoResponse':
        this.formFieldReset.emit(companyFormData.autoResponse)
        break
      case 'requestMoreInitial':
        this.formFieldReset.emit(companyFormData.requestMore.initialMessage)
        break
      case 'requestMoreFollowUp':
        this.formFieldReset.emit(companyFormData.requestMore.followUp)
        break
      case 'requestMoreWarning':
        this.formFieldReset.emit(companyFormData.requestMore.warning)
        break
      case 'requestedUpdate':
        this.formFieldReset.emit(companyFormData.requestMore.requestedUpdate)
        break
      case 'autoQuestionInitial':
        this.formFieldReset.emit(companyFormData.autoQuestions.defaultMessages?.initialMessage)
        break
      case 'autoQuestionFollowUp':
        this.formFieldReset.emit(companyFormData.autoQuestions.defaultMessages?.followUp)
        break
      case 'autoQuestionWarning':
        this.formFieldReset.emit(companyFormData.autoQuestions.defaultMessages?.warning)
        break
      case 'cultureIndexMessage':
        this.formFieldReset.emit(companyFormData.cultureIndex?.initialMessage)
        break
      case 'cultureIndexFollowUpMessage':
        this.formFieldReset.emit(companyFormData.cultureIndex?.followUp)
        break
      case 'cultureIndexWarningMessage':
        this.formFieldReset.emit(companyFormData.cultureIndex?.warning)
        break
    }
  }
}
